import { Suspense, lazy, useEffect, useState } from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useSceneTier } from '@/hooks/useSceneTier';
import { onFirstInteraction } from '@/lib/interaction';
import { GlyphField2D } from './GlyphField2D';

const GlyphFieldGL = lazy(() => import('./GlyphFieldGL'));

interface GlyphFieldProps {
  readonly words: readonly string[];
  readonly displayFont: string;
}

/**
 * The gate in front of the signature interaction. Decides which of the three
 * renderers a given visitor gets, and holds all of them until they are
 * actually warranted.
 *
 * | Condition                  | Renderer                          |
 * | --------------------------- | --------------------------------- |
 * | `useSceneTier() === 'full'`  | WebGL curl-noise field (lazy)      |
 * | `'lite'` (WebGL, but small screen / coarse pointer / not yet resolved) | 2D canvas, animated |
 * | `'none'` (no WebGL, or reduced motion) | 2D canvas, one still frame |
 * | no JS                        | nothing — headline stands alone   |
 *
 * Three hard rules from CLAUDE.md §10, all enforced here rather than inside
 * the renderers: nothing mounts until first interaction, so the scene never
 * competes with the LCP; the loop stops when scrolled offscreen; and it stops
 * when the tab is hidden. A field animating for nobody is pure battery cost.
 */
export function GlyphField({ words, displayFont }: GlyphFieldProps) {
  const reduced = useReducedMotion();
  const tier = useSceneTier();

  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const [armed, setArmed] = useState(false);
  const [onScreen, setOnScreen] = useState(true);
  const [tabVisible, setTabVisible] = useState(true);

  // `useSceneTier`'s WebGL probe is itself deferred to first interaction, but
  // this scene additionally gates its own *mount* on that same signal — the
  // probe result alone isn't enough, since 'lite' briefly describes the
  // pre-probe state too (see that hook's comment), and mounting on 'lite'
  // would flash the 2D fallback for everyone before the full-tier verdict
  // lands.
  useEffect(() => onFirstInteraction(() => setArmed(true)), []);

  useEffect(() => {
    if (!containerRef) return;
    const observer = new IntersectionObserver(
      ([entry]) => setOnScreen(entry?.isIntersecting ?? true),
      { threshold: 0 },
    );
    observer.observe(containerRef);
    return () => observer.disconnect();
  }, [containerRef]);

  useEffect(() => {
    const onVisibility = () => setTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const active = armed && onScreen && tabVisible;
  const useGL = tier === 'full';
  // Reduced motion draws exactly one frame and always takes the 2D path, so
  // it needs neither the interaction gate nor the tier probe — gating it
  // would mean a visitor who never moves the pointer never sees the
  // composition at all.
  const mounted = reduced || armed;

  return (
    <div ref={setContainerRef} aria-hidden="true" className="absolute inset-0 -z-10">
      {mounted &&
        (useGL ? (
          <Suspense fallback={null}>
            <GlyphFieldGL words={words} displayFont={displayFont} active={active} />
          </Suspense>
        ) : (
          <GlyphField2D
            words={words}
            displayFont={displayFont}
            still={reduced}
            active={active}
          />
        ))}
    </div>
  );
}
